import { BlogArticle, BlogTakeaways } from "../BlogArticle";
import { getBlogSummary } from "../blogIndex";

const post = getBlogSummary("1")!;
const galleryImages = [
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-1.jpg",
    alt: "A photo from Ahmad Danial's Penang learning journey",
  },
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-2.jpg",
    alt: "A travel moment from Ahmad Danial's Penang learning journey",
  },
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-3.jpg",
    alt: "A group learning moment during the Penang learning journey",
  },
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-5.jpg",
    alt: "A scene from the Penang learning journey",
  },
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-4.jpg",
    alt: "A Penang learning journey photo with classmates",
  },
  {
    src: "/assets/images/blog/penang-learning-journey-gallery-6.jpg",
    alt: "A landscape photo from the Penang learning journey",
  },
];

export default function PenangLearningJourneySelection() {
  return (
    <BlogArticle post={post}>
      <p>
        In 2025, something really exciting happened. I was selected to go on a
        4-day, 3-night overseas learning journey to Penang, Malaysia with my
        school!
      </p>

      <p>
        What made this special was that only 26 students were selected from the
        entire Primary 5 level. Before the final selection, shortlisted students
        had to write an essay explaining why they should be chosen. I was really
        happy when I found out that I was one of the students selected.
      </p>

      <p>
        This trip was also a big personal milestone for me because it was my
        first time travelling overseas without my parents. At first, I felt a
        little nervous, but I was also excited. It was a chance to become more
        independent, learn how to take care of myself, and work together with my
        classmates.
      </p>

      <p>
        Before writing my essay, I spent time researching Penang. I discovered
        many interesting similarities and differences between Penang and
        Singapore. I learned about their history, culture, food, languages, and
        daily life. It made me curious to experience these things for myself.
      </p>

      <p>
        The trip turned out to be an amazing experience. I got to meet new
        people, learn outside the classroom, try different foods, and understand
        more about another place that is actually quite similar to Singapore in
        many ways. Being away from home also taught me responsibility and
        confidence.
      </p>

      <p>
        Looking back, I am very grateful for the opportunity. It was one of the
        highlights of my Primary 5 year, and I learned lessons that cannot be
        found in textbooks.
      </p>

      <p>
        Below is the essay that helped me earn a place on the Penang Learning
        Journey.
      </p>

      <section className="bg-white rounded-2xl p-6 md:p-8 border border-stem-sand space-y-5">
        <h2>My Selection Essay</h2>

        <p>
          I am very excited to be shortlisted for the Penang learning journey,
          and I hope to be selected because I believe this will be a valuable
          experience for me to grow as a student and as a young Singaporean.
        </p>

        <p>
          After a lot of research, there is something unique about Penang. It is
          undoubtedly the "Singapore of the North", because they are so similar.
        </p>

        <h2>How Penang And Singapore Are Similar</h2>
        <ol className="list-decimal pl-6 space-y-4 text-stem-charcoal text-base md:text-lg leading-relaxed">
          <li>
            <strong>Penang has almost the same food as Singapore.</strong>
            <br />
            Penang has prata, laksa, nasi lemak, and so does Singapore.
            Starting from food, there are so many cultural similarities shared
            between the two places.
          </li>
          <li>
            <strong>Both Penang and Singapore were British colonies.</strong>
            <br />
            Trading ships going to China would stop by these two places. This
            gave rise to many historical similarities.
          </li>
          <li>
            <strong>Both Singapore and Penang are very multiracial.</strong>
            <br />
            Both have Chinese, Indian, Eurasians and Malays living side by side
            harmoniously every day. This is something both places can be very
            proud of.
          </li>
        </ol>

        <h2>How They Are Different</h2>
        <ol className="list-decimal pl-6 space-y-4 text-stem-charcoal text-base md:text-lg leading-relaxed">
          <li>
            <strong>Different education systems and languages.</strong>
            <br />
            In Penang, they have less exam pressure and stress. In Singapore,
            early pressure is a necessary evil to get us ready for PSLE and
            other important exams. Penang uses Bahasa Melayu as their main
            language, while Singapore uses English.
          </li>
          <li>
            <strong>Different laws and rules.</strong>
            <br />
            In Singapore, laws are way stricter than in Penang. In Singapore, it
            is banned to chew gum. But in Penang, you can eat as much chewing
            gum as you want! Unbelievable. Singapore also prioritises
            cleanliness and you can get fined for littering, spitting in public
            areas, and smoking in prohibited places.
          </li>
          <li>
            <strong>Different costs of living.</strong>
            <br />
            One Singapore dollar is worth about 3.50 Malaysian Ringgit. So a
            standard meal can cost around 3 to 4 SGD in Singapore, whilst in
            Penang a good meal can cost only 1 to 2 SGD. The same goes for
            houses, cars and furniture.
          </li>
          <li>
            <strong>Same food, different taste.</strong>
            <br />
            Even though Singapore and Penang have the same food and food names,
            they can taste completely different. My favourite comparison is
            laksa. Singapore's laksa has coconut milk and prawn while Penang has
            an asam-based stock giving it a fishy and tangy taste. This is
            something I am really looking forward to.
          </li>
        </ol>

        <p>
          I sometimes wonder how it would be if I grew up in Penang. Houses and
          cars are easy to afford. Good halal food is in abundance everywhere.
          It would also be easy to make new friends as I speak Malay. It would
          also be fun not having to stress so much for exams.
        </p>

        <p>But the grass is always greener on the other side, right?</p>

        <p>
          Thank you for shortlisting me for this learning journey. I believe it
          will inspire me to keep learning about other people in other places. I
          will also be very keen to share stories with my classmates when I
          return.
        </p>

        <p className="font-semibold text-stem-dark">
          Ahmad Danial
          <br />
          Resilience 4
        </p>
      </section>

      <section className="space-y-5">
        <div>
          <h2>Moments From The Trip</h2>
          <p>
            These are some of the photos from the learning journey. They remind
            me that the trip was not only about writing an essay or visiting
            another place. It was also about experiencing things with my
            classmates and learning how to be more independent.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryImages.map((image) => (
            <figure
              key={image.src}
              className="rounded-2xl overflow-hidden bg-white border border-stem-sand"
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
            </figure>
          ))}
        </div>
      </section>

      <BlogTakeaways
        items={[
          "Being selected felt special because only 26 Primary 5 students were chosen.",
          "Researching Penang helped me notice how similar and different it is from Singapore.",
          "Travelling without my parents helped me practise independence and responsibility.",
          "Some of the best lessons happen outside the classroom.",
        ]}
      />
    </BlogArticle>
  );
}
